// JSON client for the /pictures endpoints (PicturesController). Kept apart
// from discuss/api.ts because pictures aren't a discussion concept — the
// asset manager uses these to let any page pick or upload an image (a post
// attachment, an avatar, whatever comes next).

export type PictureRecord = {
  id: string
  url: string | null
  file_type: string
  width: number | null
  height: number | null
  file_size: number | null
  original_file_name: string | null
  created_at: string
  // Only present on the index (asset manager) payload: whether the picture
  // is already attached to a post.
  in_post?: boolean
}

function csrfToken(): string | undefined {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
}

export async function fetchPictures(picturesUrl: string): Promise<PictureRecord[] | null> {
  try {
    const response = await fetch(picturesUrl, { headers: { "Accept": "application/json" } })
    if (!response.ok) return null
    const body = (await response.json()) as { pictures: PictureRecord[] }
    return body.pictures
  } catch {
    return null
  }
}

// Multipart upload — the browser sets the content type and boundary itself.
// Returns the stored picture, or a string error to show inline.
export async function uploadPicture(picturesUrl: string, file: File): Promise<PictureRecord | string> {
  if (!csrfToken()) return "Not signed in."

  const form = new FormData()
  form.append("file", file)

  try {
    const response = await fetch(picturesUrl, {
      method: "POST",
      headers: { "Accept": "application/json", "X-CSRF-Token": csrfToken() ?? "" },
      body: form,
    })
    const payload = (await response.json()) as { picture?: PictureRecord; error?: string }
    if (!response.ok || !payload.picture) return payload.error ?? "The picture couldn't be uploaded."
    return payload.picture
  } catch {
    return "Could not reach the server."
  }
}
